import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import { motion } from 'framer-motion';

const DeductionsStoreTable = ({
  activeDepartment,
  storeRows,
  paginatedRows,
  totalPages,
  page,
  rowsPerPage,
  searchTerm,
  totalStores,
  currencyFormatter,
  onSearchTermChange,
  onRowsPerPageChange,
  onPageChange,
  onOpenStore
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.22 }}
    >
      <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
                placeholder="Search stores with deductions"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="deduction-store-rows-label">Rows</InputLabel>
                <Select
                  labelId="deduction-store-rows-label"
                  value={rowsPerPage}
                  label="Rows"
                  onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Chip
                  label={`${storeRows.length} visible stores`}
                  sx={{ bgcolor: `${activeDepartment.accent}14`, color: activeDepartment.accent, fontWeight: 700 }}
                />
                <Chip
                  label={`${totalStores} total stores`}
                  variant="outlined"
                  sx={{ borderColor: '#d1d5db', color: '#4b5563' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#ffffff' }}>
                <TableCell sx={{ fontWeight: 700 }}>Store</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  With deductions
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Without deductions
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Active
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Deduction window</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Exposure
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                      No stores have deductions set up.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Once deductions exist for this department, the store will appear here for drill-down management.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {paginatedRows.map((storeRow) => (
                <TableRow key={storeRow.store} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                      {storeRow.store}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      {storeRow.totalEmployees} employees on file
                    </Typography>
                    {storeRow.accessSource && (
                      <Tooltip
                        title={
                          <Box>
                            {storeRow.accessSource.details.map((detail) => (
                              <Typography key={detail} variant="caption" sx={{ display: 'block', color: 'white' }}>
                                {detail}
                              </Typography>
                            ))}
                          </Box>
                        }
                        arrow
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#0f766e',
                            mt: 0.5,
                            width: 'fit-content',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            cursor: 'help'
                          }}
                        >
                          {storeRow.accessSource.summary}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={storeRow.employeesWithDeductions} color="success" sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell align="center">{storeRow.employeesWithoutDeductions}</TableCell>
                  <TableCell align="center">{storeRow.activeCount}</TableCell>
                  <TableCell>{storeRow.deductionWindow}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {currencyFormatter.format(storeRow.monthlyExposure)}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => onOpenStore(storeRow.store)}
                      sx={{ textTransform: 'none', fontWeight: 700, color: activeDepartment.accent }}
                    >
                      Open store
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            p: 2.5,
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
            Showing {storeRows.length === 0 ? 0 : (page - 1) * rowsPerPage + 1} to {Math.min(page * rowsPerPage, storeRows.length)} of {storeRows.length} stores
          </Typography>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_event, nextPage) => onPageChange(nextPage)}
            size="medium"
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: activeDepartment.accent,
                color: 'white',
                '&:hover': {
                  backgroundColor: activeDepartment.accent
                }
              }
            }}
          />
        </Box>
      </Paper>
    </motion.div>
  );
};

export default DeductionsStoreTable;
